import { Breadcrumb, Spin, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FolderOpenOutlined } from '@ant-design/icons';
import { useArticlesGet } from '../../../hooks/api/article';
import { IconFileText, IconFolder, IconFolderOpen } from '../../Common/Icon';

import styles from './index.module.scss';
import NoData from '../../Common/NoData';

export default function CategoryDetails(): React.ReactNode | null {
  const {
    name, id, catName, catId,
  } = useParams();
  const navigate = useNavigate();
  const articlesGet = useArticlesGet({ autoStart: false });
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (catId) {
      articlesGet.fetch({
        pageSize: '100',
        categoryId: [catId],
        productId: id,
        status: 'published',

      });
    }
  }, [catId]);

  const articles = articlesGet.data?.data ?? [];
  const subCategoryMap: Record<string, DataNode> = {};
  const treeItems: DataNode[] = [];

  for (const article of articles) {
    const node: DataNode = {
      key: article.id,
      title: (
        <div
          className={styles.item}
          role="button"
          tabIndex={0}
          aria-label={`Open article ${article.title}`}
          onClick={() => navigate(`/article/${article.product.name}/${article.product.id}/${article.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/article/${article.id}`);
            }
          }}
        >
          <div className="icon">
            <IconFileText color="currentColor" />
          </div>
          <span>{article.title}</span>
        </div>
      ),
      isLeaf: true,
    };

    if (article.subCategory) {
      const subId = article.subCategory.id;

      if (!subCategoryMap[subId]) {
        const isExpanded = expandedKeys.includes(subId);

        subCategoryMap[subId] = {
          key: subId,
          children: [],
          title: (
            <div className={styles.item}>
              <div
                role="button"
                aria-expanded={isExpanded}
                aria-label={`Toggle ${article.subCategory.name}`}
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedKeys((prev) => (isExpanded ? prev.filter((k) => k !== subId) : [...prev, subId]));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpandedKeys((prev) => (isExpanded ? prev.filter((k) => k !== subId) : [...prev, subId]));
                  }
                }}
              >
                {isExpanded ? <IconFolderOpen /> : <IconFolder />}
              </div>
              {/* 'sub-category/:name/:productId/:catName/:id/:subName/:subId' */}
              <NavLink
                // eslint-disable-next-line max-len
                to={`/sub-category/${encodeURIComponent(name || '')}/${encodeURIComponent(id || '')}/${encodeURIComponent(catName || '')}/${encodeURIComponent(catId || '')}/${encodeURIComponent(article.subCategory.name || '')}/${article.subCategory.id}`}
                className={styles.subTitle}
              >
                {article.subCategory.name}
              </NavLink>

            </div>
          ),
        };
        treeItems.push(subCategoryMap[subId]);
      }
      subCategoryMap[subId].children!.push(node);
    } else {
      treeItems.push(node);
    }
  }

  return (
    <div className="wrapper">
      <div className="breadcrumb">
        <Breadcrumb
          items={[
            { title: <NavLink to="/" className="breadcrumbLink">Help portal</NavLink> },
            {
              title: (
                <NavLink
                  to={`/product/${id}/${encodeURIComponent(name || '')}`}
                  className="breadcrumbLink"
                >
                  {name}
                </NavLink>
              ),
            },
            { title: `${catName}` },
          ]}
        />
      </div>
      <h2 className={styles.title}>{catName}</h2>

      <Tree
        treeData={treeItems}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        showIcon={false}
        selectable={false}
        switcherIcon={null}
        className="categotyTree"
      />
      {!articlesGet.loading && treeItems.length === 0 && (
        <NoData />
      )}
      {articlesGet.loading ? (
        <div className="spin">
          <Spin />
        </div>
      ) : null}
    </div>
  );
}
